import type { DiagramData } from './lessons';

export interface GlossaryEntry {
  term: string;
  definition: string;
  sport?: 'football' | 'soccer' | 'basketball' | 'baseball' | 'all';
  diagram?: DiagramData;
}

/**
 * Sports glossary — terms are matched case-insensitively inside lesson text.
 * Keep terms sorted alphabetically within each sport group.
 */
export const glossary: GlossaryEntry[] = [
  // ── Football ──────────────────────────────────────────────
  {
    term: 'blitz',
    definition: 'A defensive play where extra players rush the quarterback beyond the normal pass rush, aiming to pressure or sack them before they can throw.',
    sport: 'football',
    diagram: {
      title: 'Linebacker Blitz',
      sport: 'football',
      players: [
        { x: 30, y: 56, type: 'offense', label: 'LT' },
        { x: 40, y: 56, type: 'offense', label: 'LG' },
        { x: 50, y: 56, type: 'offense', label: 'C' },
        { x: 60, y: 56, type: 'offense', label: 'RG' },
        { x: 70, y: 56, type: 'offense', label: 'RT' },
        { x: 50, y: 67, type: 'offense', label: 'QB' },
        { x: 35, y: 50, type: 'defense' },
        { x: 50, y: 50, type: 'defense' },
        { x: 65, y: 50, type: 'defense' },
        { x: 42, y: 44, type: 'defense', label: 'LB' },
        { x: 58, y: 44, type: 'defense', label: 'LB' },
      ],
      routes: [
        { fromX: 42, fromY: 44, toX: 45, toY: 58, type: 'dashed' },
        { fromX: 58, fromY: 44, toX: 55, toY: 58, type: 'dashed' },
      ],
    },
  },
  {
    term: 'line of scrimmage',
    definition: 'The imaginary line across the field where the ball is placed before a play begins. Neither team may cross it before the snap.',
    sport: 'football',
  },
  {
    term: 'pocket',
    definition: 'The protected area behind the offensive line where the quarterback stands to throw. Formed by the blockers pushing defenders outward.',
    sport: 'football',
  },
  {
    term: 'snap',
    definition: 'The action of the center passing the ball between their legs to the quarterback or punter to start a play.',
    sport: 'football',
  },
  {
    term: 'shotgun',
    definition: 'A formation where the quarterback lines up 5–7 yards behind the center to receive the snap, giving more time to read the defense.',
    sport: 'football',
  },
  {
    term: 'play-action',
    definition: 'A passing play designed to look like a run. The quarterback fakes a handoff before throwing, freezing linebackers and safeties.',
    sport: 'football',
  },
  {
    term: 'audible',
    definition: 'When the quarterback changes the play at the line of scrimmage by calling out a new set of signals after reading the defense.',
    sport: 'football',
  },
  {
    term: 'red zone',
    definition: 'The area between the opponent\'s 20-yard line and the goal line. Scoring efficiency here is a key metric for offenses.',
    sport: 'football',
  },
  {
    term: 'sack',
    definition: 'When a defensive player tackles the quarterback behind the line of scrimmage before they can throw the ball.',
    sport: 'football',
  },
  {
    term: 'formation',
    definition: 'The arrangement of offensive or defensive players on the field before the snap. Examples: I-Formation, Shotgun, 4-3 Defense.',
    sport: 'football',
  },
  {
    term: 'zone coverage',
    definition: 'A pass defense scheme where defenders guard areas of the field rather than specific receivers.',
    sport: 'football',
  },
  {
    term: 'man coverage',
    definition: 'A pass defense scheme where each defender is assigned to cover a specific offensive player one-on-one.',
    sport: 'football',
  },
  {
    term: 'tight end',
    definition: 'A versatile position that lines up on the offensive line. Can block like a lineman or run routes like a receiver.',
    sport: 'football',
  },

  // ── Soccer ────────────────────────────────────────────────
  {
    term: 'offside',
    definition: 'A player is offside if they are closer to the opponent\'s goal line than both the ball and the second-to-last defender when the ball is played to them.',
    sport: 'soccer',
  },
  {
    term: 'press',
    definition: 'A high-intensity defensive tactic where players aggressively close down the opponent in possession to force turnovers high up the pitch.',
    sport: 'soccer',
  },
  {
    term: 'through ball',
    definition: 'A forward pass played into space behind the defensive line for a teammate to run onto, often leading to a goal-scoring chance.',
    sport: 'soccer',
  },
  {
    term: 'false nine',
    definition: 'A tactical role where the center forward drops deep into midfield, pulling center-backs out of position and creating space for wingers.',
    sport: 'soccer',
  },
  {
    term: 'back line',
    definition: 'The row of defenders (usually 3–5) positioned nearest to their own goal, forming the last line of defense before the goalkeeper.',
    sport: 'soccer',
  },
  {
    term: 'box-to-box',
    definition: 'A midfielder who covers the full length of the pitch, contributing to both defense and attack throughout the match.',
    sport: 'soccer',
  },
  {
    term: 'clean sheet',
    definition: 'When a team\'s goalkeeper and defense prevent the opposition from scoring any goals in a match.',
    sport: 'soccer',
  },
  {
    term: 'counterattack',
    definition: 'A rapid offensive transition after winning possession, exploiting the opponent\'s forward positioning before they can recover defensively.',
    sport: 'soccer',
  },
  {
    term: 'set piece',
    definition: 'A planned play from a dead-ball situation like a free kick, corner kick, or throw-in. Teams rehearse specific movements and positioning.',
    sport: 'soccer',
  },
  {
    term: 'overlap',
    definition: 'When a player (usually a fullback) runs past a teammate with the ball on the outside, creating a 2v1 situation on the wing.',
    sport: 'soccer',
  },

  // ── Basketball ────────────────────────────────────────────
  {
    term: 'pick and roll',
    definition: 'An offensive play where a player sets a screen (pick) for the ball-handler, then moves toward the basket (rolls) to receive a pass.',
    sport: 'basketball',
    diagram: {
      title: 'Pick and Roll',
      sport: 'basketball',
      players: [
        { x: 50, y: 55, type: 'offense', label: 'PG' },
        { x: 48, y: 50, type: 'offense', label: '5' },
        { x: 52, y: 50, type: 'defense' },
        { x: 50, y: 60, type: 'defense' },
      ],
      routes: [
        { fromX: 50, fromY: 55, toX: 60, toY: 50, type: 'solid' },
        { fromX: 48, fromY: 50, toX: 48, toY: 35, type: 'dashed' },
      ],
    },
  },
  {
    term: 'fast break',
    definition: 'A quick transition from defense to offense, pushing the ball up the court before the opposing team can set up their defense.',
    sport: 'basketball',
  },
  {
    term: 'triple threat',
    definition: 'The position a player takes when they catch the ball — feet set, knees bent — from which they can shoot, pass, or dribble.',
    sport: 'basketball',
  },
  {
    term: 'paint',
    definition: 'The rectangular area under the basket (also called the lane or key). A dominant position for scoring and rebounding.',
    sport: 'basketball',
  },
  {
    term: 'screen',
    definition: 'When an offensive player stands still to block a defender\'s path, freeing a teammate to shoot, pass, or drive to the basket.',
    sport: 'basketball',
  },
  {
    term: 'post up',
    definition: 'When a player positions themselves near the basket with their back to it, using their body to create space for a close-range shot.',
    sport: 'basketball',
  },
  {
    term: 'zone defense',
    definition: 'A defensive strategy where each player guards a specific area of the court rather than an individual opponent. Common types: 2-3, 3-2.',
    sport: 'basketball',
  },
  {
    term: 'turnover',
    definition: 'When the offensive team loses possession of the ball to the defense, through a steal, bad pass, or violation.',
    sport: 'basketball',
  },
  {
    term: 'assist',
    definition: 'A pass to a teammate that directly leads to a made basket. A key stat measuring a player\'s playmaking ability.',
    sport: 'basketball',
  },
  {
    term: 'spacing',
    definition: 'How players position themselves on the court to maintain optimal distance from each other, preventing the defense from helping easily.',
    sport: 'basketball',
  },
];

/** Build a case-insensitive lookup map for fast matching */
const glossaryMap = new Map<string, GlossaryEntry>();
glossary.forEach(entry => {
  glossaryMap.set(entry.term.toLowerCase(), entry);
});

export function lookupGlossaryTerm(term: string): GlossaryEntry | undefined {
  return glossaryMap.get(term.toLowerCase());
}

/**
 * Build a regex that matches any glossary term in text.
 * Matches whole words only, case-insensitive.
 * Optionally filter by sport.
 */
export function buildGlossaryRegex(sportFilter?: string): RegExp | null {
  const terms = glossary
    .filter(e => !sportFilter || e.sport === sportFilter || e.sport === 'all')
    .map(e => e.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .sort((a, b) => b.length - a.length); // longest first to avoid partial matches

  if (terms.length === 0) return null;
  return new RegExp(`\\b(${terms.join('|')})\\b`, 'gi');
}
